import { useState, useEffect, useRef } from "react";
import { photoFilenames, videoData } from "./constants";
import { Helmet, HelmetData } from "react-helmet-async";
import {
  CloseIcon,
  DownloadIcon,
  MaximizeIcon,
  NextIcon,
  PauseIcon,
  PauseIconAlt,
  PlayIcon,
  PlayIconAlt,
  PlayVideoIcon,
  PrevIcon,
  SpeakerIcon,
  SpeakerMutedIcon,
  StartSlideshowIcon,
} from "../../../icons/galleryIcons";

const helmetData = new HelmetData({});

export default function EnhancedGallery() {
  const [isLoading, setIsLoading] = useState(true);
  const [photos, setPhotos] = useState([]);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [isSlideshow, setIsSlideshow] = useState(false);
  const [activeTab, setActiveTab] = useState("photos");
  // const [videoPlaying, setVideoPlaying] = useState(null);
  const [loadingVideo, setLoadingVideo] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [videoBuffered, setVideoBuffered] = useState(0);

  const slideshowTimerRef = useRef(null);
  const videoRef = useRef(null);
  const slideshowIntervalMs = 3000; // 3 seconds per slide

  useEffect(() => {
    setIsLoading(true);
    const importedPhotos = photoFilenames;
    setPhotos(importedPhotos);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    return () => {
      if (slideshowTimerRef.current) {
        clearInterval(slideshowTimerRef.current);
      }
    };
  }, []);

  // Handle slideshow timer
  useEffect(() => {
    if (isSlideshow && selectedMedia !== null && activeTab === "photos") {
      // Start the slideshow timer
      slideshowTimerRef.current = setInterval(() => {
        setSelectedMedia((prev) => {
          if (typeof prev === "number") {
            return (prev + 1) % photos.length;
          }
          return prev;
        });
      }, slideshowIntervalMs);

      // Return cleanup function
      return () => {
        if (slideshowTimerRef.current) {
          clearInterval(slideshowTimerRef.current);
          slideshowTimerRef.current = null;
        }
      };
    }
  }, [isSlideshow, selectedMedia, photos.length, activeTab]);

  // Handle video progress updates
  const updateVideoProgress = () => {
    if (videoRef.current) {
      const progress =
        (videoRef.current.currentTime / videoRef.current.duration) * 100;
      setVideoProgress(progress);

      // Update buffered amount
      if (videoRef.current.buffered.length > 0) {
        const bufferedEnd = videoRef.current.buffered.end(
          videoRef.current.buffered.length - 1
        );
        const bufferedPercent = (bufferedEnd / videoRef.current.duration) * 100;
        setVideoBuffered(bufferedPercent);
      }
    }
  };

  const startSlideshow = () => {
    if (photos.length > 0 && activeTab === "photos") {
      setSelectedMedia(0);
      setIsSlideshow(true);
    }
  };

  const stopSlideshow = () => {
    setIsSlideshow(false);
    if (slideshowTimerRef.current) {
      clearInterval(slideshowTimerRef.current);
      slideshowTimerRef.current = null;
    }
  };

  const openLightbox = (index, type = "photo") => {
    setSelectedMedia(type === "photo" ? index : { type: "video", id: index });
    setIsSlideshow(false);
  };

  const closeLightbox = () => {
    setSelectedMedia(null);
    setIsSlideshow(false);
    // setVideoPlaying(null);
    if (videoRef.current) {
      videoRef.current.pause();
    }
  };

  const nextPhoto = () => {
    if (activeTab === "photos" && typeof selectedMedia === "number") {
      setSelectedMedia((prev) => (prev + 1) % photos.length);
    }
  };

  const prevPhoto = () => {
    if (activeTab === "photos" && typeof selectedMedia === "number") {
      setSelectedMedia((prev) => (prev - 1 + photos.length) % photos.length);
    }
  };

  const playVideo = (videoId) => {
    // setVideoPlaying(videoId);
    setLoadingVideo(true);

    // If in lightbox, focus on the video
    if (selectedMedia && typeof selectedMedia !== "number") {
      if (videoRef.current) {
        videoRef.current.play().catch((error) => {
          console.error("Error playing video:", error);
        });
      }
    } else {
      // Open the video in lightbox
      openLightbox(videoId, "video");
    }
  };

  const handleDownloadPhoto = async () => {
    const url = photos[selectedMedia];
    const fileName = url.split("/").pop() || "download";

    try {
      const response = await fetch(url, { mode: "cors" });
      const blob = await response.blob();

      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedMedia === null) return;

      switch (e.key) {
        case "ArrowRight":
          if (activeTab === "photos" && typeof selectedMedia === "number") {
            nextPhoto();
          }
          break;
        case "ArrowLeft":
          if (activeTab === "photos" && typeof selectedMedia === "number") {
            prevPhoto();
          }
          break;
        case "Escape":
          closeLightbox();
          break;
        case " ": // Space bar
          if (activeTab === "photos") {
            setIsSlideshow((prev) => !prev);
          } else if (videoRef.current) {
            if (videoRef.current.paused) {
              videoRef.current.play();
            } else {
              videoRef.current.pause();
            }
            e.preventDefault(); // Prevent page scroll
          }
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
    // eslint-disable-next-line
  }, [selectedMedia, activeTab]);

  // Get current video data if a video is selected
  const getCurrentVideo = () => {
    if (selectedMedia && typeof selectedMedia !== "number") {
      return videoData.find((video) => video.id === selectedMedia.id);
    }
    return null;
  };

  const currentVideo = getCurrentVideo();

  return (
    <>
      <Helmet helmetData={helmetData}>
        <title>
          Photo &amp; Video Gallery | [AI] Data Stack Connect 2025
        </title>
        <link
          rel="canonical"
          href="https://analyticsintelligence.com/event/ai-data-stack-connect-2025/gallery"
        />
        <meta
          name="description"
          content="Browse the photo and video gallery from [AI] Data Stack Connect 2025 — the Analytics Intelligence event on AI agents and the modern data stack, held in London."
        />
        <meta
          name="keywords"
          content="[AI] Data Stack Connect 2025, event photo gallery, event video gallery, Analytics Intelligence event, AI agents, London data event"
        />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Analytics Intelligence Ltd" />
        <meta
          property="og:title"
          content="Photo & Video Gallery | [AI] Data Stack Connect 2025"
        />
        <meta
          property="og:description"
          content="Browse photos and videos from [AI] Data Stack Connect 2025 — the Analytics Intelligence event on AI agents and the modern data stack, held in London."
        />
        <meta
          property="og:url"
          content="https://analyticsintelligence.com/event/ai-data-stack-connect-2025/gallery"
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          content="https://analyticsintelligence.com/favicon.png"
        />
        <meta property="og:site_name" content="Analytics Intelligence" />
        <meta property="og:locale" content="en_GB" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content="@_AnalyticsIntel" />
        <meta
          name="twitter:title"
          content="Photo & Video Gallery | [AI] Data Stack Connect 2025"
        />
        <meta
          name="twitter:description"
          content="Browse photos and videos from [AI] Data Stack Connect 2025 — the Analytics Intelligence event held in London."
        />
        <meta
          name="twitter:image"
          content="https://analyticsintelligence.com/favicon.png"
        />
      </Helmet>
      <main className="min-h-screen bg-white">
        {/* Header */}
        <header className="p-6 flex flex-col md:flex-row justify-between items-start md:items-center bg-[#2F4171] text-white bg-ai_event_hero_bg bg-cover sticky top-0 left-0 z-[2]">
          <div>
            <h1 className="text-2xl font-bold mt-2">
              [AI] Data Stack Connect 2025
            </h1>
            <p className="text-sm opacity-80">
              From Data to Insight with AI Agents
            </p>
          </div>

          <div className="flex items-center space-x-4 mt-4 md:mt-0">
            {activeTab === "photos" && (
              <button
                onClick={startSlideshow}
                className="flex items-center px-4 py-2 btn-sweep btn-sweep-indigo rounded-md"
                aria-label="Start slideshow"
              >
                <StartSlideshowIcon />
                Slideshow
              </button>
            )}
          </div>
        </header>

        {/* Tabs */}
        <div className="container mx-auto px-4 pt-8">
          <div className="flex justify-center mb-8 border-b">
            <button
              onClick={() => setActiveTab("photos")}
              className={`px-6 py-3 font-medium text-lg ${
                activeTab === "photos"
                  ? "text-[#4F6AE5] border-b-2 border-[#4F6AE5]"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              Photos ({photos.length})
            </button>
            <button
              onClick={() => setActiveTab("videos")}
              className={`px-6 py-3 font-medium text-lg ${
                activeTab === "videos"
                  ? "text-[#4F6AE5] border-b-2 border-[#4F6AE5]"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              Videos ({videoData.length})
            </button>
          </div>
        </div>

        {/* Gallery Content */}
        <main className="container mx-auto px-4 pb-8">
          <h2 className="font-sora text-3xl lg:text-[36px] leading-[150%] font-semibold text-[#2F4171] text-center">
            Event {activeTab === "photos" ? "Photo" : "Video"} Gallery
          </h2>
          <p className="text-[#1C1C1C] font-[300] leading-[150%] text-center mb-8">
            Browse all {activeTab === "photos" ? "photos" : "videos"} from the
            [AI] Data Stack Connect event
          </p>

          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <p>Loading media...</p>
            </div>
          ) : activeTab === "photos" ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {photos.map((photo, index) => (
                <div
                  key={index}
                  className="group relative aspect-[4/3] overflow-hidden rounded-lg shadow-md"
                >
                  <img
                    src={photo || "/placeholder.svg"}
                    alt={photo}
                    className="w-full h-full object-cover hover:opacity-90 cursor-pointer hover:object-contain transition-all"
                    onClick={() => openLightbox(index, "photo")}
                    loading="lazy"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white p-2 text-sm opacity-0 group-hover:opacity-100 transition-opacity">
                    {photo.split("/").pop().split(".")[0]}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
              {videoData.map((video) => (
                <div
                  key={video.id}
                  className="group relative aspect-video overflow-hidden rounded-lg shadow-md bg-gray-100"
                >
                  <div className="absolute inset-0 flex items-center justify-center">
                    <img
                      src={video.thumbnailUrl || "/placeholder.svg"}
                      alt={video.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />

                    {/* Play button overlay */}
                    <button
                      onClick={() => playVideo(video.id)}
                      className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 group-hover:bg-opacity-40 transition-all"
                      aria-label={`Play ${video.title}`}
                    >
                      <div className="w-16 h-16 rounded-full bg-white bg-opacity-70 hover:bg-opacity-100 flex items-center justify-center">
                        <PlayVideoIcon />
                      </div>
                    </button>
                  </div>

                  {/* Video info */}
                  <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-white p-3">
                    <h3 className="font-medium text-ellipsis overflow-hidden whitespace-nowrap">
                      {video.title}
                    </h3>
                    <p className="text-sm opacity-80">
                      {video.date}, ({video.time})
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>

        {/* Lightbox */}
        {selectedMedia !== null && (
          <div className="fixed inset-0 bg-black bg-opacity-95 z-50 flex items-center justify-center">
            {/* Close button */}
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 text-white text-2xl p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors z-10"
              aria-label="Close lightbox"
            >
              <CloseIcon />
            </button>

            {typeof selectedMedia === "number" ? (
              <>
                {/* Photo Lightbox Content */}
                {/* Slideshow controls */}
                <div className="absolute top-4 left-4 flex items-center space-x-4">
                  {isSlideshow ? (
                    <button
                      onClick={stopSlideshow}
                      className="flex items-center space-x-2 btn-sweep btn-sweep-glass text-white px-3 py-1 rounded-md"
                    >
                      <PauseIconAlt />
                      <span>Pause</span>
                    </button>
                  ) : (
                    <button
                      onClick={() => setIsSlideshow(true)}
                      className="flex items-center space-x-2 btn-sweep btn-sweep-glass text-white px-3 py-1 rounded-md"
                    >
                      <PlayIconAlt />
                      <span>Play</span>
                    </button>
                  )}
                  <span className="text-white text-sm">
                    {selectedMedia + 1} / {photos.length}
                  </span>
                </div>

                {/* Previous button */}
                <button
                  onClick={prevPhoto}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-white p-2 rounded-full bg-black bg-opacity-50 hover:bg-opacity-70 transition-colors"
                  aria-label="Previous photo"
                >
                  <PrevIcon />
                </button>

                {/* Main image */}
                <div className="text-center">
                  <img
                    src={photos[selectedMedia] || "/placeholder.svg"}
                    alt={`[AI] Data Stack Connect event ${
                      selectedMedia + 1
                    }`}
                    className="max-h-[80vh] max-w-[90vw] object-contain"
                  />
                  <p className="text-white mt-2 text-sm">
                    {photos[selectedMedia].split("/").pop().split(".")[0]}
                  </p>
                </div>

                {/* Next button */}
                <button
                  onClick={nextPhoto}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white p-2 rounded-full bg-black bg-opacity-50 hover:bg-opacity-70 transition-colors"
                  aria-label="Next photo"
                >
                  <NextIcon />
                </button>

                {/* Download current photo button */}
                <button
                  onClick={handleDownloadPhoto}
                  className="absolute bottom-4 right-4 flex items-center space-x-2 btn-sweep btn-sweep-glass text-white px-3 py-1 rounded-md"
                >
                  <DownloadIcon />
                  <span>Download</span>
                </button>
              </>
            ) : (
              <>
                {/* Video Lightbox Content */}
                <div className="w-full max-w-5xl relative">
                  {/* Video player */}
                  <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
                    {loadingVideo && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-70 z-10">
                        <div className="w-16 h-16 border-4 border-t-[#4F6AE5] border-r-transparent border-b-transparent border-l-transparent rounded-full animate-spin"></div>
                      </div>
                    )}

                    <video
                      ref={videoRef}
                      src={currentVideo?.url}
                      className="w-full h-full"
                      controls={false}
                      autoPlay
                      playsInline
                      preload="metadata"
                      poster={currentVideo?.thumbnailUrl}
                      onTimeUpdate={updateVideoProgress}
                      onLoadedData={() => setLoadingVideo(false)}
                      onWaiting={() => setLoadingVideo(true)}
                      onPlaying={() => setLoadingVideo(false)}
                    />

                    {/* Custom video controls */}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4">
                      {/* Progress bar */}
                      <div
                        className="relative w-full h-1 bg-gray-600 rounded-full mb-4 cursor-pointer"
                        onClick={(e) => {
                          if (videoRef.current) {
                            const rect =
                              e.currentTarget.getBoundingClientRect();
                            const pos = (e.clientX - rect.left) / rect.width;
                            videoRef.current.currentTime =
                              pos * videoRef.current.duration;
                          }
                        }}
                      >
                        {/* Buffered progress */}
                        <div
                          className="absolute top-0 left-0 h-full bg-gray-400 rounded-full"
                          style={{ width: `${videoBuffered}%` }}
                        ></div>
                        {/* Playback progress */}
                        <div
                          className="absolute top-0 left-0 h-full bg-[#4F6AE5] rounded-full"
                          style={{ width: `${videoProgress}%` }}
                        ></div>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          {/* Play/Pause button */}
                          <button
                            onClick={() => {
                              if (videoRef.current) {
                                if (videoRef.current.paused) {
                                  videoRef.current.play();
                                } else {
                                  videoRef.current.pause();
                                }
                              }
                            }}
                            className="text-white hover:text-[#4F6AE5] transition-colors"
                            aria-label={
                              videoRef.current?.paused ? "Play" : "Pause"
                            }
                          >
                            {videoRef.current?.paused ? (
                              <PlayIcon />
                            ) : (
                              <PauseIcon />
                            )}
                          </button>

                          {/* Volume control */}
                          <button
                            onClick={() => {
                              if (videoRef.current) {
                                videoRef.current.muted =
                                  !videoRef.current.muted;
                              }
                            }}
                            className="text-white hover:text-[#4F6AE5] transition-colors"
                            aria-label="Toggle mute"
                          >
                            {videoRef.current && videoRef.current.muted ? (
                              <SpeakerMutedIcon />
                            ) : (
                              <SpeakerIcon />
                            )}
                          </button>
                        </div>

                        {/* Video title */}
                        <div className="text-white">
                          <h3 className="font-medium">{currentVideo?.title}</h3>
                        </div>

                        {/* Fullscreen button */}
                        <button
                          onClick={() => {
                            if (videoRef.current) {
                              if (document.fullscreenElement) {
                                document.exitFullscreen();
                              } else {
                                videoRef.current.requestFullscreen();
                              }
                            }
                          }}
                          className="text-white hover:text-[#4F6AE5] transition-colors"
                          aria-label="Toggle fullscreen"
                        >
                          <MaximizeIcon />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Video info and download */}
                  <div className="flex justify-between items-center mt-4">
                    <div className="text-white">
                      <h3 className="text-xl font-medium">
                        {currentVideo?.title}, ({currentVideo.time})
                      </h3>
                      <p className="text-gray-400">{currentVideo?.date}</p>
                    </div>

                    <a
                      href={currentVideo?.url}
                      download={currentVideo?.title}
                      className="flex items-center space-x-2 btn-sweep btn-sweep-indigo text-white px-4 py-2 rounded-md cursor-pointer"
                    >
                      <DownloadIcon />
                      <span>Download Video</span>
                    </a>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </main>
    </>
  );
}
